import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertTriangle, ArrowLeft, CheckCircle2, Loader2, RefreshCw, Search } from 'lucide-react';
import Header from '../components/Header';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '../components/ui/sheet';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '../components/ui/table';
import { authService } from '../services/authService';
import { rastreioEntregaService } from '../services/rastreioEntregaService';
import { toast } from '../hooks/use-toast';
import {
  EventoRastreio,
  LinhaDoTempoRastreio,
  ListaEventosRastreio,
  ResumoInstancia,
  ResumoRastreio
} from '../types/rastreioEntrega';

type Periodo = '1h' | '24h' | '7d';
const PERIODO_MS: Record<Periodo, number> = { '1h': 3600e3, '24h': 24 * 3600e3, '7d': 7 * 24 * 3600e3 };
const PERIODO_LABEL: Record<Periodo, string> = { '1h': 'Última hora', '24h': 'Últimas 24h', '7d': '7 dias' };

// Limites usados no diagnóstico automático
const LIM = {
  filaMs: 10_000,       // espera na fila acima disso = servidor congestionado
  apiMs: 5_000,         // provedor demorando para aceitar o envio
  entradaMs: 60_000,    // mensagem chegou ao sistema muito depois de enviada pelo cliente
  procMs: 5_000         // nosso backend demorando para processar a mensagem recebida
};

const ETAPA_LABEL: Record<string, string> = {
  envio_fila: 'Enviada à API (via fila)',
  envio_direto: 'Enviada à API (sem fila)',
  envio_erro: 'Erro no envio',
  tentativa_erro: 'Tentativa de envio falhou',
  fila_timeout: 'Fila excedeu 60s',
  status: 'Confirmação do WhatsApp',
  status_orfao: 'Confirmação sem mensagem',
  recebida: 'Mensagem recebida',
  eco_envio: 'Eco do envio',
  conexao: 'Conexão'
};

function fmtMs(ms: number | null | undefined): string {
  if (ms === null || ms === undefined) return '—';
  const abs = Math.abs(ms);
  const sinal = ms < 0 ? '-' : '';
  if (abs < 1000) return `${sinal}${abs}ms`;
  if (abs < 60_000) return `${sinal}${(abs / 1000).toFixed(1)}s`;
  if (abs < 3600_000) return `${sinal}${Math.floor(abs / 60_000)}min ${Math.round((abs % 60_000) / 1000)}s`;
  return `${sinal}${Math.floor(abs / 3600_000)}h ${Math.round((abs % 3600_000) / 60_000)}min`;
}

function fmtHora(iso: string | null): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit' });
}

function eventoTemProblema(e: EventoRastreio): boolean {
  return !!e.erro
    || ['envio_erro', 'tentativa_erro', 'fila_timeout', 'status_orfao'].includes(e.etapa)
    || e.status === 'falhou'
    || (e.direcao === 'conexao' && e.status !== 'connected')
    || (e.atrasoMs ?? 0) >= LIM.entradaMs
    || (e.filaEsperaMs ?? 0) >= LIM.filaMs
    || (e.apiDuracaoMs ?? 0) >= LIM.apiMs;
}

/** Medida principal do evento, para exibir na lista. */
function medidaEvento(e: EventoRastreio): string {
  if (e.direcao === 'entrada' || e.etapa === 'status') {
    const partes = [];
    if (e.atrasoMs !== null) partes.push(`atraso ${fmtMs(e.atrasoMs)}`);
    if (e.processamentoMs !== null) partes.push(`proc. ${fmtMs(e.processamentoMs)}`);
    return partes.join(' · ') || '—';
  }
  const partes = [];
  if (e.filaEsperaMs !== null) partes.push(`fila ${fmtMs(e.filaEsperaMs)}`);
  if (e.apiDuracaoMs !== null) partes.push(`API ${fmtMs(e.apiDuracaoMs)}`);
  if (e.filaPendentes) partes.push(`${e.filaPendentes} na frente`);
  if (e.tentativas) partes.push(`${e.tentativas} tent.`);
  return partes.join(' · ') || '—';
}

interface Alerta { nivel: 'warn' | 'crit'; titulo: string; texto: string; }

/** Traduz os números em hipóteses de causa. */
function diagnosticar(resumo: ResumoRastreio | null): Alerta[] {
  if (!resumo) return [];
  const alertas: Alerta[] = [];
  const nome = (r: { provedor: string | null; instancia: string | null }) => `${r.instancia || '?'} (${r.provedor || '?'})`;

  resumo.porInstancia.forEach((r: ResumoInstancia) => {
    if (r.quedas_conexao > 0) {
      alertas.push({ nivel: 'crit', titulo: `Conexão instável: ${nome(r)}`,
        texto: `A instância caiu/reconectou ${r.quedas_conexao}x no período. Mensagens ficam paradas enquanto o celular da instância estiver desconectado — verifique internet/bateria do aparelho conectado.` });
    }
    if ((r.fila_p95_ms ?? 0) >= LIM.filaMs || r.timeouts_fila > 0) {
      alertas.push({ nivel: 'crit', titulo: `Fila de envio congestionada: ${nome(r)}`,
        texto: `5% dos envios esperaram mais de ${fmtMs(r.fila_p95_ms)} na fila do servidor${r.timeouts_fila ? ` e ${r.timeouts_fila} passaram de 60s` : ''}. A fila envia 1 mensagem/s para todas as empresas — o atraso é do nosso servidor, não do cliente.` });
    }
    if ((r.api_p95_ms ?? 0) >= LIM.apiMs) {
      alertas.push({ nivel: 'warn', titulo: `Provedor lento para aceitar envios: ${nome(r)}`,
        texto: `5% das chamadas à API levaram mais de ${fmtMs(r.api_p95_ms)}. Indica lentidão na ${r.provedor === 'META' ? 'Meta' : 'Uazapi'} ou na conexão do servidor com ela.` });
    }
    if (r.erros_envio > 0 || r.falhas_status > 0) {
      alertas.push({ nivel: 'crit', titulo: `Falhas de envio: ${nome(r)}`,
        texto: `${r.erros_envio} erro(s) ao enviar e ${r.falhas_status} mensagem(ns) recusada(s) pelo WhatsApp. Ative "apenas problemas" abaixo para ver o motivo de cada uma.` });
    }
    if ((r.entrada_atraso_p95_ms ?? 0) >= LIM.entradaMs) {
      alertas.push({ nivel: 'warn', titulo: `Mensagens recebidas chegando atrasadas: ${nome(r)}`,
        texto: `5% das mensagens recebidas chegaram ao sistema mais de ${fmtMs(r.entrada_atraso_p95_ms)} depois do horário em que foram enviadas no WhatsApp. Atraso ANTES do nosso servidor: celular do cliente sem internet, celular da instância offline ou demora do provedor.` });
    }
    if ((r.entrada_proc_p95_ms ?? 0) >= LIM.procMs) {
      alertas.push({ nivel: 'warn', titulo: `Processamento lento no servidor: ${nome(r)}`,
        texto: `5% das mensagens recebidas levaram mais de ${fmtMs(r.entrada_proc_p95_ms)} para serem processadas pelo nosso backend (download de mídia, banco). Atraso DO NOSSO lado.` });
    }
    if (r.status_sem_mensagem > 0) {
      alertas.push({ nivel: 'warn', titulo: `Confirmações sem mensagem: ${nome(r)}`,
        texto: `${r.status_sem_mensagem} confirmação(ões) de entrega/leitura chegaram mas não encontramos a mensagem no banco — o tique de entregue/lido não aparece para o atendente nesses casos.` });
    }
  });

  resumo.entrega.forEach((e) => {
    if (e.sem_entregue > 0) {
      alertas.push({ nivel: 'warn', titulo: `${e.sem_entregue} mensagem(ns) sem confirmação de entrega (${e.is_grupo ? 'grupos' : 'privado'}): ${nome(e)}`,
        texto: `Foram aceitas pelo WhatsApp há mais de 5 min mas o aparelho do destinatário ainda não confirmou o recebimento. Normalmente: celular do cliente desligado/sem internet, ou número bloqueou a instância.` });
    }
  });

  return alertas;
}

const DiagnosticoEntregaPage: React.FC = () => {
  const navigate = useNavigate();
  const user = authService.getCurrentUser();
  const isGestor = user?.role === 'Admin' || user?.role === 'SuperAdmin';

  const [periodo, setPeriodo] = useState<Periodo>('24h');
  const [resumo, setResumo] = useState<ResumoRastreio | null>(null);
  const [eventos, setEventos] = useState<ListaEventosRastreio | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [apenasProblemas, setApenasProblemas] = useState(true);
  const [direcao, setDirecao] = useState<string>('');
  const [pagina, setPagina] = useState(1);
  const [buscaId, setBuscaId] = useState('');

  const [timeline, setTimeline] = useState<LinhaDoTempoRastreio | null>(null);
  const [timelineId, setTimelineId] = useState<string | null>(null);
  const [timelineLoading, setTimelineLoading] = useState(false);

  useEffect(() => {
    if (user && !isGestor) navigate('/dashboard');
  }, [user, isGestor, navigate]);

  const carregar = useCallback(async (silencioso = false) => {
    if (!silencioso) setLoading(true); else setRefreshing(true);
    const desde = new Date(Date.now() - PERIODO_MS[periodo]).toISOString();
    try {
      const [r, ev] = await Promise.all([
        rastreioEntregaService.getResumo(desde),
        rastreioEntregaService.getEventos({ desde, apenasProblemas, direcao: direcao || undefined, pagina, porPagina: 50 })
      ]);
      setResumo(r);
      setEventos(ev);
    } catch (error) {
      console.error('Erro ao carregar diagnóstico de entrega:', error);
      toast({ title: 'Não foi possível carregar o diagnóstico de entrega.', variant: 'destructive' });
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [periodo, apenasProblemas, direcao, pagina]);

  useEffect(() => {
    if (isGestor) carregar();
  }, [isGestor, carregar]);

  const abrirTimeline = useCallback(async (apiMessageId: string) => {
    setTimelineId(apiMessageId);
    setTimeline(null);
    setTimelineLoading(true);
    try {
      setTimeline(await rastreioEntregaService.getPorApiMessageId(apiMessageId));
    } catch (error) {
      toast({ title: 'Não foi possível carregar o rastreio da mensagem.', variant: 'destructive' });
    } finally {
      setTimelineLoading(false);
    }
  }, []);

  const alertas = useMemo(() => diagnosticar(resumo), [resumo]);
  const totalPaginas = eventos ? Math.max(1, Math.ceil(eventos.total / eventos.porPagina)) : 1;

  if (!isGestor) return null;

  const filtroBtn = (ativo: boolean) =>
    `px-3 py-1 rounded-md text-xs font-medium transition-colors ${ativo
      ? 'bg-sky-600 text-white'
      : 'text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-slate-700'}`;

  return (
    <div className="flex flex-col h-[100dvh] bg-gray-100 dark:bg-slate-900 overflow-hidden">
      <Header titulo="Diagnóstico de Entrega" user={user} />

      <div className="flex-1 overflow-y-auto p-4 md:p-6 flex flex-col gap-4">
        <div className="flex flex-wrap items-center gap-3">
          <Button variant="ghost" size="sm" className="gap-1.5 -ml-2" onClick={() => navigate('/dashboard')}>
            <ArrowLeft className="h-4 w-4" />
            Voltar para atendimentos
          </Button>
          <div className="flex items-center gap-1 bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-lg p-1">
            {(Object.keys(PERIODO_LABEL) as Periodo[]).map((p) => (
              <button key={p} className={filtroBtn(periodo === p)} onClick={() => { setPeriodo(p); setPagina(1); }}>
                {PERIODO_LABEL[p]}
              </button>
            ))}
          </div>
          <Button variant="outline" size="sm" className="gap-1.5" onClick={() => carregar(true)} disabled={refreshing}>
            <RefreshCw className={`h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} />
            Atualizar
          </Button>
          <form
            className="flex items-center gap-2 ml-auto"
            onSubmit={(e) => { e.preventDefault(); if (buscaId.trim()) abrirTimeline(buscaId.trim()); }}
          >
            <Input
              value={buscaId}
              onChange={(e) => setBuscaId(e.target.value)}
              placeholder="ID da mensagem no WhatsApp"
              className="h-8 w-64 text-xs"
            />
            <Button type="submit" size="sm" variant="outline" className="gap-1.5"><Search className="h-4 w-4" />Rastrear</Button>
          </form>
        </div>

        {loading ? (
          <div className="flex-1 flex items-center justify-center text-gray-400">
            <Loader2 className="h-6 w-6 animate-spin" />
          </div>
        ) : (
          <>
            {/* Diagnóstico automático */}
            <div className="flex flex-col gap-2">
              {alertas.length === 0 ? (
                <Card className="dark:bg-slate-800 dark:border-slate-700">
                  <CardContent className="p-4 flex items-center gap-2 text-sm text-emerald-600 dark:text-emerald-400">
                    <CheckCircle2 className="h-4 w-4" />
                    Nenhum atraso ou falha relevante no período.
                    {resumo && resumo.porInstancia.length === 0 && ' (Ainda não há dados — o rastreio começa a contar a partir desta versão.)'}
                  </CardContent>
                </Card>
              ) : alertas.map((a, i) => (
                <Card key={i} className={`border-l-4 dark:bg-slate-800 dark:border-slate-700 ${a.nivel === 'crit' ? 'border-l-red-500' : 'border-l-amber-500'}`}>
                  <CardContent className="p-3">
                    <div className={`flex items-center gap-2 text-sm font-semibold ${a.nivel === 'crit' ? 'text-red-600 dark:text-red-400' : 'text-amber-600 dark:text-amber-400'}`}>
                      <AlertTriangle className="h-4 w-4" />{a.titulo}
                    </div>
                    <p className="text-xs text-gray-600 dark:text-gray-300 mt-1">{a.texto}</p>
                  </CardContent>
                </Card>
              ))}
            </div>

            {/* Por instância */}
            <Card className="dark:bg-slate-800 dark:border-slate-700">
              <CardContent className="p-0">
                <div className="px-4 pt-3 pb-1 text-sm font-semibold dark:text-white">Tempos por instância <span className="text-[11px] font-normal text-gray-400">(mediana / pior 5%)</span></div>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Instância</TableHead>
                      <TableHead className="text-right">Enviadas</TableHead>
                      <TableHead className="text-right">Espera na fila</TableHead>
                      <TableHead className="text-right">Resposta da API</TableHead>
                      <TableHead className="text-right">Recebidas</TableHead>
                      <TableHead className="text-right">Atraso até chegar</TableHead>
                      <TableHead className="text-right">Processamento</TableHead>
                      <TableHead className="text-right">Erros</TableHead>
                      <TableHead className="text-right">Quedas</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {(resumo?.porInstancia || []).map((r, i) => (
                      <TableRow key={i}>
                        <TableCell className="font-medium">{r.instancia || '—'} <Badge variant="secondary" className="ml-1">{r.provedor || '?'}</Badge></TableCell>
                        <TableCell className="text-right">{r.enviadas}</TableCell>
                        <TableCell className="text-right">{fmtMs(r.fila_p50_ms)} / {fmtMs(r.fila_p95_ms)}</TableCell>
                        <TableCell className="text-right">{fmtMs(r.api_p50_ms)} / {fmtMs(r.api_p95_ms)}</TableCell>
                        <TableCell className="text-right">{r.recebidas}</TableCell>
                        <TableCell className="text-right">{fmtMs(r.entrada_atraso_p50_ms)} / {fmtMs(r.entrada_atraso_p95_ms)}</TableCell>
                        <TableCell className="text-right">{fmtMs(r.entrada_proc_p50_ms)} / {fmtMs(r.entrada_proc_p95_ms)}</TableCell>
                        <TableCell className="text-right">{r.erros_envio + r.falhas_status + r.timeouts_fila}</TableCell>
                        <TableCell className="text-right">{r.quedas_conexao}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>

            {/* Entrega privado x grupo */}
            <Card className="dark:bg-slate-800 dark:border-slate-700">
              <CardContent className="p-0">
                <div className="px-4 pt-3 pb-1 text-sm font-semibold dark:text-white">Do envio até o aparelho do destinatário</div>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Instância</TableHead>
                      <TableHead>Tipo</TableHead>
                      <TableHead className="text-right">Enviadas</TableHead>
                      <TableHead className="text-right">Confirmadas</TableHead>
                      <TableHead className="text-right">Sem confirmação (&gt;5min)</TableHead>
                      <TableHead className="text-right">Até entregue (mediana / pior 5%)</TableHead>
                      <TableHead className="text-right">Até lida (mediana)</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {(resumo?.entrega || []).map((e, i) => (
                      <TableRow key={i}>
                        <TableCell className="font-medium">{e.instancia || '—'} <Badge variant="secondary" className="ml-1">{e.provedor || '?'}</Badge></TableCell>
                        <TableCell>{e.is_grupo ? 'Grupo' : 'Privado'}</TableCell>
                        <TableCell className="text-right">{e.enviadas}</TableCell>
                        <TableCell className="text-right">{e.com_entregue}</TableCell>
                        <TableCell className={`text-right ${e.sem_entregue > 0 ? 'text-amber-600 dark:text-amber-400 font-semibold' : ''}`}>{e.sem_entregue}</TableCell>
                        <TableCell className="text-right">{fmtMs(e.entregue_p50_ms)} / {fmtMs(e.entregue_p95_ms)}</TableCell>
                        <TableCell className="text-right">{fmtMs(e.lida_p50_ms)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>

            {/* Eventos */}
            <Card className="dark:bg-slate-800 dark:border-slate-700">
              <CardContent className="p-0">
                <div className="px-4 pt-3 pb-2 flex flex-wrap items-center gap-3">
                  <span className="text-sm font-semibold dark:text-white">Eventos</span>
                  <div className="flex items-center gap-1 bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-lg p-1">
                    <button className={filtroBtn(apenasProblemas)} onClick={() => { setApenasProblemas(true); setPagina(1); }}>Apenas problemas</button>
                    <button className={filtroBtn(!apenasProblemas)} onClick={() => { setApenasProblemas(false); setPagina(1); }}>Todos</button>
                  </div>
                  <div className="flex items-center gap-1 bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-lg p-1">
                    {[['', 'Tudo'], ['saida', 'Envio'], ['entrada', 'Recebimento'], ['conexao', 'Conexão']].map(([v, l]) => (
                      <button key={v} className={filtroBtn(direcao === v)} onClick={() => { setDirecao(v); setPagina(1); }}>{l}</button>
                    ))}
                  </div>
                  <span className="text-[11px] text-gray-400 ml-auto">{eventos?.total ?? 0} evento(s) — clique para ver o caminho completo da mensagem</span>
                </div>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Quando</TableHead>
                      <TableHead>Instância</TableHead>
                      <TableHead>Etapa</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Tempos</TableHead>
                      <TableHead>Erro</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {(eventos?.itens || []).map((e) => (
                      <TableRow
                        key={e.id}
                        className={`${e.apiMessageId ? 'cursor-pointer' : ''} ${eventoTemProblema(e) ? 'bg-red-50/60 dark:bg-red-950/20' : ''}`}
                        onClick={() => e.apiMessageId && abrirTimeline(e.apiMessageId)}
                      >
                        <TableCell className="whitespace-nowrap text-xs">{fmtHora(e.registradoEm)}</TableCell>
                        <TableCell className="text-xs">{e.instancia || '—'} <span className="text-gray-400">{e.provedor}</span></TableCell>
                        <TableCell className="text-xs">
                          {ETAPA_LABEL[e.etapa] || e.etapa}
                          {e.isGrupo && <Badge variant="outline" className="ml-1 text-[10px]">grupo</Badge>}
                        </TableCell>
                        <TableCell className="text-xs">{e.status || '—'}</TableCell>
                        <TableCell className="text-xs whitespace-nowrap">{medidaEvento(e)}</TableCell>
                        <TableCell className="text-xs max-w-[360px] truncate" title={e.erro || ''}>{e.erro || ''}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                <div className="flex items-center justify-end gap-2 px-4 py-2">
                  <Button size="sm" variant="outline" disabled={pagina <= 1} onClick={() => setPagina(p => p - 1)}>Anterior</Button>
                  <span className="text-xs text-gray-500">{pagina} / {totalPaginas}</span>
                  <Button size="sm" variant="outline" disabled={pagina >= totalPaginas} onClick={() => setPagina(p => p + 1)}>Próxima</Button>
                </div>
              </CardContent>
            </Card>
          </>
        )}
      </div>

      {/* Linha do tempo de uma mensagem */}
      <Sheet open={!!timelineId} onOpenChange={(o) => { if (!o) { setTimelineId(null); setTimeline(null); } }}>
        <SheetContent className="w-full sm:max-w-lg overflow-y-auto">
          <SheetHeader>
            <SheetTitle>Caminho da mensagem</SheetTitle>
            <SheetDescription className="break-all text-xs">{timelineId}</SheetDescription>
          </SheetHeader>
          {timelineLoading ? (
            <div className="flex justify-center py-10 text-gray-400"><Loader2 className="h-6 w-6 animate-spin" /></div>
          ) : timeline && timeline.eventos.length > 0 ? (
            <div className="mt-4 flex flex-col gap-3">
              {timeline.resumo && (
                <div className="text-xs bg-gray-50 dark:bg-slate-800 rounded-md p-3 flex flex-col gap-1">
                  <div>Envio → entregue no aparelho: <b>{fmtMs(timeline.resumo.enviadaAteEntregueMs)}</b></div>
                  <div>Envio → lida: <b>{fmtMs(timeline.resumo.enviadaAteLidaMs)}</b></div>
                  {timeline.resumo.falhou && <div className="text-red-600 font-semibold">A mensagem falhou</div>}
                </div>
              )}
              <ol className="relative border-l border-gray-200 dark:border-slate-700 ml-2">
                {timeline.eventos.map((e) => (
                  <li key={e.id} className="mb-4 ml-4">
                    <span className={`absolute -left-1.5 mt-1.5 h-3 w-3 rounded-full ${eventoTemProblema(e) ? 'bg-red-500' : 'bg-sky-500'}`} />
                    <div className="text-xs text-gray-400">{fmtHora(e.registradoEm)}{e.eventoEm && ` · no WhatsApp: ${fmtHora(e.eventoEm)}`}</div>
                    <div className="text-sm font-medium dark:text-white">
                      {ETAPA_LABEL[e.etapa] || e.etapa}{e.status ? `: ${e.status}` : ''}
                    </div>
                    <div className="text-xs text-gray-500">{medidaEvento(e)}</div>
                    {e.erro && <div className="text-xs text-red-600 break-words mt-1">{e.erro}</div>}
                    {e.detalhes && (
                      <pre className="text-[10px] text-gray-400 whitespace-pre-wrap break-all mt-1">{JSON.stringify(e.detalhes)}</pre>
                    )}
                  </li>
                ))}
              </ol>
            </div>
          ) : (
            <div className="text-sm text-gray-500 py-10 text-center">Nenhum evento registrado para esta mensagem.</div>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
};

export default DiagnosticoEntregaPage;
